import { useCallback, useRef } from 'react';
import { HubConnectionState } from '@microsoft/signalr';
import { useSignalRHub, type ConnectionStatus } from '@/realtime/useSignalRHub';
import type { LpHubAccountDto, NopFiltersDto, RiskDashboardLiveDto } from '@/api/types/riskDashboard';

interface RiskDashboardLiveOptions {
  enabled: boolean;
  initialFilters: NopFiltersDto;
  onData: (data: RiskDashboardLiveDto) => void;
  onError?: (message: string) => void;
}

// Main Risk Dashboard live hub (/hubs/risk-dashboard-live). Subscribes with the
// current NOP filters on (re)connect, and exposes updateFilters() to push filter
// changes (UpdateFilters) — mirrors risk-dashboard-connection.js.
export function useRiskDashboardLive({
  enabled,
  initialFilters,
  onData,
  onError,
}: RiskDashboardLiveOptions): { status: ConnectionStatus; updateFilters: (dto: NopFiltersDto) => void } {
  const filtersRef = useRef<NopFiltersDto>(initialFilters);

  const { status, connection } = useSignalRHub({
    url: '/hubs/risk-dashboard-live',
    enabled,
    on: {
      OnRiskDashboardDataReceived: (data) => onData(data as RiskDashboardLiveDto),
      OnRiskDashboardError: (message) => onError?.(String(message ?? 'An error occurred while fetching live exposure.')),
    },
    onConnected: (conn) => {
      void conn.invoke('Subscribe', filtersRef.current).catch(() => {
        onError?.('Failed to connect to live exposure updates.');
      });
    },
  });

  const updateFilters = useCallback(
    (dto: NopFiltersDto) => {
      filtersRef.current = dto;
      const conn = connection.current;
      if (conn && conn.state === HubConnectionState.Connected) {
        void conn.invoke('UpdateFilters', dto).catch(() => onError?.('Failed to update live filters.'));
      }
    },
    [connection, onError],
  );

  return { status, updateFilters };
}

interface LpAccountsLiveOptions {
  enabled: boolean;
  onAccounts: (accounts: LpHubAccountDto[]) => void;
  onError?: (message: string) => void;
}

// Secondary LP-accounts live hub (/hubs/lp-accounts-live). The server only streams
// to connections that have joined the tenant group, which happens when the client
// invokes Subscribe() — so onConnected must call it, exactly like the standalone LP
// Accounts page (features/tenant/lpAccounts/useLpAccountsLive). Without it the hub
// connects but never pushes, leaving the RD LP Accounts table empty.
export function useLpAccountsLive({ enabled, onAccounts, onError }: LpAccountsLiveOptions): {
  status: ConnectionStatus;
} {
  const { status } = useSignalRHub({
    url: '/hubs/lp-accounts-live',
    enabled,
    on: {
      OnLPAccountsReceived: (data) => {
        const payload = data as { accounts?: LpHubAccountDto[] } | null;
        onAccounts(payload?.accounts ?? []);
      },
      OnLPAccountsError: (message) => onError?.(String(message ?? 'LP accounts live update failed.')),
    },
    onConnected: (conn) => {
      void conn.invoke('Subscribe').catch(() => onError?.('Failed to connect to live LP accounts.'));
    },
  });
  return { status };
}
